<template>
  <div>
    <b-button
      v-b-tooltip.hover
      :variant="hasGps ? 'primary' : 'secondary'"
      :class="{'px-2 py-0': sm}"
      :disabled="saving_coords"
      :title="hasGps ? 'View/Update GPS Location' : 'Set GPS Location'"
      @click="handleGPSButtonClick"
    >
      <i class="fal fa-map-pin" />
    </b-button>

    <b-modal :id="'select-modal-'+sample.id" title="Sample GPS" hide-footer>
      <div>
        <b-row class="no-gutters">
          <b-col class="mr-3">
            <b-button v-b-modal="'use-gps-modal-'+sample.id" variant="dark" block class="py-3 h-100 d-flex flex-column align-items-center justify-content-center">
              <i class="far fa-list font-2xl mb-2" />
              <span>
                Use GPS Location
              </span>
            </b-button>
          </b-col>
          <b-col>
            <b-button v-b-modal="'new-gps-modal-'+sample.id" variant="dark" block class="py-3 h-100 d-flex flex-column align-items-center justify-content-center">
              <i class="far fa-map-marker-alt font-2xl mb-2" />
              <span>
                New GPS Location
              </span>
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <b-modal
      :id="'new-gps-modal-'+sample.id"
      title="Set New GPS Location"
      size="lg"
      :ok-disabled="!newGPS.lat || !newGPS.long || saving_coords || loading_customer_coords"
      @shown="onNewGPSModalShown"
      @hidden="onNewGPSModalClosed"
      @ok="updateSampleCoords"
    >
      <div class="mb-2">
        <MapboxMap :initial-coordinates="initialGPSMapCoordinates" show-geolocate-control @location-selected="handleMarkerChange" />
      </div>

      <div class="d-flex align-items-center">
        <b-form-checkbox
          v-model="newGPS.save_gps_location"
          name="save_gps_location"
          :value="true"
          :unchecked-value="false"
          class="py-2"
        >
          <span class="text-nowrap">Save GPS Location</span>
        </b-form-checkbox>

        <div v-if="newGPS.save_gps_location" class="w-100">
          <b-form-group label="GPS Location Name" label-cols="3" label-align="right" label-class="font-weight-bold" class="mb-0">
            <b-form-input
              v-model="newGPS.gps_location_name"
              name="gps_location_name"
              placeholder="GPS Location Name"
              :disabled="!newGPS.save_gps_location"
            />
          </b-form-group>
        </div>
      </div>

      <b-alert v-if="saving_coords_error" show variant="danger" class="mt-1 mb-0">
        {{ saving_coords_error }}
      </b-alert>

      <template #modal-ok>
        <i v-if="saving_coords" class="fal fa-spinner fa-spin text-center" />
        <span v-else>Save Location</span>
      </template>
    </b-modal>

    <b-modal
      :id="'use-gps-modal-'+sample.id"
      title="Use Saved GPS Location"
      size="xl"
      :ok-disabled="!selected_customer_coord || saving_coords || loading_customer_coords"
      :no-close-on-backdrop="saving_coords"
      :no-close-on-esc="saving_coords"
      @shown="onExistingGPSModalShown"
      @hidden="onExistingGPSModalClosed"
      @ok="updateSampleCoords"
    >
      <b-row class="no-gutters">
        <b-col cols="3" class="mr-3">
          <b-form-input
            v-model="searchTermGps"
            placeholder="Search locations..."
            class="mb-2"
          />
          <b-list-group style="max-height: 400px; overflow-y: auto;">
            <b-list-group-item v-if="loading_customer_coords" disabled>
              <div class="d-flex align-items-center justify-content-center">
                <i class="fal fa-circle-notch fa-spin mr-2" />
                <span>Loading...</span>
              </div>
            </b-list-group-item>

            <template v-else-if="filteredCustomerCoords.length">
              <b-list-group-item
                v-for="(customer_coord, index) in filteredCustomerCoords"
                :key="index"
                button
                :active="!!(selected_customer_coord && selected_customer_coord.id === customer_coord.id)"
                @click="selected_customer_coord = customer_coord"
              >
                {{ customer_coord.name }}
              </b-list-group-item>
            </template>

            <b-list-group-item v-else disabled>
              <div class="d-flex align-items-center justify-content-center">
                <span>No GPS Locations Found</span>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-col>

        <b-col>
          <MapboxMap :selected-customer-coord="selected_customer_coord" :interactive="false" />
        </b-col>
      </b-row>
      <b-alert v-if="saving_coords_error" show variant="danger" class="mt-1 mb-0">
        {{ saving_coords_error }}
      </b-alert>

      <template #modal-ok>
        <i v-if="saving_coords" class="fal fa-spinner fa-spin text-center" />
        <span v-else>Use Selected Location</span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import MapboxMap from '@/components/shared/MapboxMap.vue'
import CustomerCoordCollection from '@/collections/crm/CustomerCoordCollection'
import CustomerCoordModel from '@/models/crm/CustomerCoordModel'
import ToastNotifications from '@/components/shared/mixins/ToastNotifications'

export default {
  name: 'SampleCoordsManager',
  components: {
    MapboxMap
  },
  mixins: [ToastNotifications],
  props: {
    sample: {
      type: Object,
      required: true
    },
    sm: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      customer_id: this.$store.getters.getAuthCustomerID,
      searchTermGps: '',
      newGPS: {
        lat: null,
        long: null,
        save_gps_location: false,
        gps_location_name: null
      },
      customer_coords_collection: null,
      loading_customer_coords: false,
      selected_customer_coord: null,
      saving_coords: false,
      saving_coords_error: null
    }
  },
  computed: {
    hasGps () {
      return !!(this.sample && this.sample.lat && this.sample.long)
    },
    filteredCustomerCoords () {
      if (!this.customer_coords_collection || !this.customer_coords_collection.models) {
        return []
      }
      let coords = this.customer_coords_collection.models

      if (this.searchTermGps) {
        const searchTermLower = this.searchTermGps.toLowerCase()
        coords = coords.filter(coord =>
          coord.name && coord.name.toLowerCase().includes(searchTermLower)
        )
      }

      return _.sortBy(coords, ['name'])
    },
    initialGPSMapCoordinates () {
      if (this.newGPS.lat && this.newGPS.long) {
        return { lat: this.newGPS.lat, lng: this.newGPS.long }
      }
      if (this.sample && this.sample.lat && this.sample.long) {
        return { lat: parseFloat(this.sample.lat), lng: parseFloat(this.sample.long) }
      }
      return null
    }
  },
  methods: {
    handleGPSButtonClick () {
      this.newGPS.lat = null
      this.newGPS.long = null
      this.newGPS.save_gps_location = false
      this.newGPS.gps_location_name = null

      if (this.sample && this.sample.lat && this.sample.long) {
        this.newGPS.lat = parseFloat(this.sample.lat)
        this.newGPS.long = parseFloat(this.sample.long)
        // Directly show the new GPS modal
        this.$bvModal.show('new-gps-modal-'+this.sample.id)
      } else {
        // No existing GPS data, show the selection modal
        this.$bvModal.show('select-modal-'+this.sample.id)
      }
    },

    onNewGPSModalShown () {
      this.$bvModal.hide('select-modal-'+this.sample.id)
      this.fetchCustomerCoords()
    },

    onNewGPSModalClosed () {
      this.newGPS.lat = null
      this.newGPS.long = null
      this.newGPS.save_gps_location = false
      this.newGPS.gps_location_name = null
      this.saving_coords_error = null
    },

    handleMarkerChange (lngLat) {
      this.newGPS.lat = lngLat.lat
      this.newGPS.long = lngLat.long
    },

    onExistingGPSModalShown () {
      this.$bvModal.hide('select-modal-'+this.sample.id)
      this.fetchCustomerCoords()
    },

    onExistingGPSModalClosed () {
      this.selected_customer_coord = null
      this.searchTermGps = ''
      this.saving_coords_error = null
    },

    fetchCustomerCoords () {
      this.loading_customer_coords = true
      const collection = new CustomerCoordCollection()
      collection.fetch().then(() => {
        this.customer_coords_collection = collection
        this.loading_customer_coords = false
      }).catch(() => {
        this.quickDangerToast('Error', 'Could not load saved GPS locations.')
        this.loading_customer_coords = false
      })
    },

    async updateSampleCoords (bvModalEvent) {
      bvModalEvent.preventDefault()
      this.saving_coords = true
      this.saving_coords_error = null

      // Validate GPS coordinates
      if (this.newGPS.save_gps_location && this.customer_coords_collection) {
        const duplicate = this.customer_coords_collection.models.find(coord => coord.name === this.newGPS.gps_location_name)
        if (duplicate) {
          this.saving_coords_error = 'Duplicate GPS Location Name'
          this.saving_coords = false
          return
        }
      }

      // Set sample coordinates
      if (this.selected_customer_coord) {
        this.sample.set('lat', this.selected_customer_coord.lat)
        this.sample.set('long', this.selected_customer_coord.long)
        this.sample.set('customer_coord_id', this.selected_customer_coord.id)
      } else {
        this.sample.set('lat', this.newGPS.lat)
        this.sample.set('long', this.newGPS.long)
      }

      this.sample.set('update_reason', 'GPS Coordinates Updated')

      try {
        if (this.newGPS.save_gps_location) {
          const customerCoord = new CustomerCoordModel({
            name: this.newGPS.gps_location_name,
            lat: this.newGPS.lat,
            long: this.newGPS.long,
            customer_id: this.customer_id
          })
          const savedNewCoord = await customerCoord.save()
          this.sample.set('customer_coord_id', savedNewCoord.id)
        }

        await this.sample.save()

        this.quickSuccessToast('Success', 'Sample coordinates saved successfully')
        this.saving_coords = false
        this.$bvModal.hide('use-gps-modal-'+this.sample.id)
        this.$bvModal.hide('new-gps-modal-'+this.sample.id)
        this.sample_coordinates = {
          lat: parseFloat(this.sample.lat),
          lng: parseFloat(this.sample.long)
        }

        this.$emit('coords-updated', {
          id: this.sample.id,
          lat: this.sample.lat,
          long: this.sample.long,
          customer_coord_id: this.sample.customer_coord_id
        })
      } catch (error) {
        this.saving_coords_error = error.message || 'Error saving sample coordinates. Please try again.'
        this.quickDangerToast('Error', this.saving_coords_error)
        this.saving_coords = false
      }
    }
  }
}
</script>
